<template>
  <v-card class="step-tab-panel" flat>
    <div
      class="actions-container pa-0 ma-0 flex-grow-0"
      :style="[{ height: showSpacebreakConfigurations ? '62px' : '52px' }]"
    >
      <v-row class="ma-0">
        <v-col
          class="d-flex align-center spacebreak-actions"
          :class="[showSpacebreakConfigurations ? 'px-3 py-2' : 'pa-3']"
        >
          <span class="mr-3 font-weight-bold">
            {{ tkey('reviewSpacebreakMessage') }}

            <!-- Spacebreak calculator tooltip -->
            <DocsLink link="toolguide/04f-spacebreaks.html" />
          </span>
          <v-btn
            v-if="!hasSpacebreaks"
            :disabled="isEditingDisabled || isSBJobRunning"
            data-id-e2e="btnCalculateSpacebreaks"
            class="button-space-5"
            primary
            @click="onCalculateSpacebreakClick(false)"
          >
            {{ tkey('calculateSpacebreaks') }}
          </v-btn>
        </v-col>

        <v-col v-if="showSpacebreakConfigurations" class="spacebreak-configurations pa-2">
          <div class="d-flex">
            <div
              v-for="(v, c) in spacebreakConfigurations"
              :key="`${c}-configuration`"
              class="d-flex align-center"
            >
              <span class="mr-2 spacebreak-configurations__label">
                {{ tkey(`configurations.${c}`) }}:
              </span>
              <v-text-field
                v-model.number="spacebreakConfigurations[c]"
                :disabled="isEditingDisabled"
                grey
                class="spacebreak-configurations__input mr-2"
                hide-details
                :rules="rules[c]"
                @change="updateDiff(c, $event)"
              />
            </div>
          </div>
          <p class="mb-0 mt-1 spacebreak-configurations__message">
            {{ tkey('configurations.infoMessage') }}
          </p>
        </v-col>
      </v-row>
    </div>

    <div fluid class="pa-0 flex-grow-1 d-flex flex-column">
      <v-layout v-if="!hasSpacebreaks || isSBJobRunning" class="empty-switching">
        <v-row class="main-panel">
          <v-col cols="2" class="rtls-border rtls-border--right" />
          <v-col cols="10" class="pl-0 pr-0 pb-0">
            <ProgressBar
              v-if="isSBJobRunning"
              :message="$t('messages.inProgress')"
              :percentage="jobStatuses.spacebreakGenerator.progress"
              :status="jobStatuses.spacebreakGenerator.status"
            />

            <div v-else class="d-flex align-center justify-center flex-column" style="height: 100%">
              <p class="empty-switching-text">
                {{ tkey('spacebreaksNotGenerated') }}
              </p>
              <p class="empty-switching-text">
                {{ tkey('runToView') }}
              </p>
            </div>
          </v-col>
        </v-row>
      </v-layout>

      <template v-if="hasSpacebreaks && !isSBJobRunning">
        <v-row class="main-panel ma-0">
          <v-col cols="12" class="pa-0 d-flex flex-column h-100">
            <v-tabs
              v-model="selectedTab"
              class="store-class-tabs"
              density="compact"
              :hide-slider="true"
            >
              <v-tab
                v-for="item in getStoreClassesInScenario"
                :key="item._id"
                :value="item._id"
                class="store-class-tab text-none step-tab"
              >
                {{ item.name }}
                <ErrorTriangle
                  v-if="storeClassesWithInvalidSpacebreaksMap[item._id]"
                  :class="'error-triangle ml-2'"
                  :server-error="
                    tkey('invalidSpacebreaks', [
                      storeClassesWithInvalidSpacebreaksMap[item._id]?.length || 0,
                    ])
                  "
                  :tooltip-location="'end'"
                />
                <br />
              </v-tab>
            </v-tabs>

            <v-window v-model="selectedTab" class="flex-grow-1">
              <v-window-item
                v-for="item in getStoreClassesInScenario"
                :key="item._id"
                :value="item._id"
                class="h-100"
              >
                <Spacebreaks :store-class-id="item._id" />
              </v-window-item>
            </v-window>
          </v-col>
        </v-row>

        <div class="page-actions-container flex-grow-0">
          <PageActions
            :show-export="showNotImplemented"
            :has-data-changes="hasDataChanges"
            :has-data-errors="hasDataErrors"
            :save-loading="isLoading || isSaving"
            :save-disabled="isEditingDisabled || isSaving"
            :is-discard-enabled="!isEditingDisabled && !isSaving"
            @discard="discard"
            @save="save"
          >
            <template #right-btns>
              <div class="calculate-btn-container d-flex align-center">
                <v-btn
                  :disabled="isEditingDisabled || isSBJobRunning"
                  data-id-e2e="btnReCalculateSpacebreaks"
                  primary
                  @click="onCalculateSpacebreakClick(false)"
                >
                  {{ tkey('reCalculateSpacebreaks') }}
                </v-btn>
              </div>
            </template>
          </PageActions>
        </div>
      </template>
    </div>
    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="spacebreakCalculator"
      @close="closeDependencyTreeModal"
      @commit="commitHandler"
    />

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="confirmUnsavedDataModal"
    />
  </v-card>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useInputValidations } from '@/composables/input-validations';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useFeature } from '@/composables/use-feature';
import { useScenarioStatusFormatter } from '@/composables/use-scenario-status-formatter';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';
import { useAiStore } from '@/stores/ai';
import { useContextStore } from '@/stores/context';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useFurnitureStore } from '@/stores/furniture';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import numberUtils from '@/utils/number-format-utils';
import furnitureType from '@enums/furniture-types';
import {
  cloneDeep,
  difference,
  each,
  every,
  filter,
  find,
  findIndex,
  flatMap,
  get,
  isEmpty,
  isEqual,
  isUndefined,
  keyBy,
  map,
  omit,
  orderBy,
  padStart,
  reject,
  round,
  size,
  some,
  sortBy,
  startsWith,
  uniqBy,
  union,
  keys,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

const newlyAddedSpacebreakIdPrefix = 'newlyAddedSpacebreak';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },

  setup() {
    const { canEditSpacebreakCalculatorPage } = usePermission();

    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();
    const { tkey } = useInternationalization('spacebreakCalculatorPage');

    // AI Panel setup
    const instance = getCurrentInstance();
    const furnitureStore = useFurnitureStore();
    const { aiPanelEnabled } = useFeature();
    const { formatScenarioProgress } = useScenarioStatusFormatter('spacebreak-calculator');

    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,
      getExclude: () => {
        if (instance.proxy.isEditingDisabled || instance.proxy.isSBJobRunning)
          return [
            'rename-spacebreak',
            'update-fill-override',
            'add-spacebreak',
            'remove-spacebreak',
            'add-furniture-to-spacebreak',
            'remove-furniture-from-spacebreak',
            'save-spacebreaks',
            'discard-spacebreak-changes',
            'recalculate-spacebreaks',
          ];
        return [];
      },
      actions: {
        'rename-spacebreak': {
          description:
            'Rename a spacebreak. Use the _id fields from the storeClasses context for storeClassId and spacebreakId. ' +
            'The new name must be unique within the store class. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Renaming spacebreak',
          confirmationLabel: args => `rename spacebreak to "${args.newName}"`,
          parameters: {
            type: 'object',
            properties: {
              storeClassId: {
                type: 'string',
                description: 'The _id of the store class containing the spacebreak.',
              },
              spacebreakId: { type: 'string', description: 'The _id of the spacebreak to rename.' },
              newName: { type: 'string', description: 'The new unique name for the spacebreak.' },
            },
            required: ['storeClassId', 'spacebreakId', 'newName'],
          },
          handler: async ({ storeClassId, spacebreakId, newName }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (instance.proxy.isSBJobRunning)
              return '[Tool] Action failed: a spacebreak generation job is running. Ask the user to wait until it completes.';
            if (!storeClassId || !spacebreakId || !newName?.trim())
              return '[Tool] Action failed: storeClassId, spacebreakId, and newName are all required.';
            const storeClass = furnitureStore.getStoreClassesInScenario.find(
              sc => sc._id === storeClassId
            );
            if (!storeClass)
              return `[Tool] Action failed: store class with _id "${storeClassId}" not found.`;
            const spacebreak = storeClass.spacebreaks?.find(sb => sb._id === spacebreakId);
            if (!spacebreak)
              return `[Tool] Action failed: spacebreak with _id "${spacebreakId}" not found in store class "${storeClass.name}".`;
            const isDuplicate = storeClass.spacebreaks.some(
              sb => sb._id !== spacebreakId && sb.name?.trim() === newName.trim()
            );
            if (isDuplicate)
              return `[Tool] Action failed: the name "${newName}" is already used by another spacebreak in store class "${storeClass.name}". Ask the user for a different name.`;
            furnitureStore.updateSpacebreakForStoreClass({
              storeClassId,
              updatedSpacebreak: { _id: spacebreakId, name: newName.trim() },
            });
            return `[Tool] Action succeeded: renamed spacebreak to "${newName.trim()}" in store class "${storeClass.name}".`;
          },
        },
        'update-fill-override': {
          description:
            'Set the fill override value of a spacebreak. Use _id fields from the storeClasses context. ' +
            'The value must be a positive number and unique within the store class. ' +
            'Changing fill override counts as a structural edit and will trigger a dependency check on save. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Updating fill override',
          confirmationLabel: args => `set fill override to ${args.newValue}`,
          parameters: {
            type: 'object',
            properties: {
              storeClassId: { type: 'string', description: 'The _id of the store class.' },
              spacebreakId: { type: 'string', description: 'The _id of the spacebreak.' },
              newValue: { type: 'number', description: 'The new positive fill override value.' },
            },
            required: ['storeClassId', 'spacebreakId', 'newValue'],
          },
          handler: async ({ storeClassId, spacebreakId, newValue }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (instance.proxy.isSBJobRunning)
              return '[Tool] Action failed: a spacebreak generation job is running. Ask the user to wait until it completes.';
            if (!storeClassId || !spacebreakId)
              return '[Tool] Action failed: storeClassId and spacebreakId are required.';
            const parsedValue = Number(newValue);
            if (Number.isNaN(parsedValue) || parsedValue <= 0)
              return `[Tool] Action failed: newValue must be a positive number. Received "${newValue}".`;
            const storeClass = furnitureStore.getStoreClassesInScenario.find(
              sc => sc._id === storeClassId
            );
            if (!storeClass)
              return `[Tool] Action failed: store class with _id "${storeClassId}" not found.`;
            const spacebreak = storeClass.spacebreaks?.find(sb => sb._id === spacebreakId);
            if (!spacebreak)
              return `[Tool] Action failed: spacebreak with _id "${spacebreakId}" not found in store class "${storeClass.name}".`;
            const isDuplicate = storeClass.spacebreaks.some(
              sb => sb._id !== spacebreakId && Number(sb.fillOverride) === parsedValue
            );
            if (isDuplicate)
              return `[Tool] Action failed: fill override ${parsedValue} is already used by another spacebreak in store class "${storeClass.name}". Each spacebreak must have a unique fill override.`;
            furnitureStore.updateSpacebreakForStoreClass({
              storeClassId,
              updatedSpacebreak: { _id: spacebreakId, fillOverride: parsedValue },
            });
            return `[Tool] Action succeeded: set fill override to ${parsedValue} on spacebreak "${spacebreak.name || spacebreakId}" in store class "${storeClass.name}".`;
          },
        },
        'add-spacebreak': {
          description:
            'Add a new empty spacebreak to a store class. Use the storeClass _id from the storeClasses context. ' +
            'After adding, set its name, fill override, and assign furniture before saving. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Adding spacebreak',
          confirmationLabel: args => {
            const sc = furnitureStore.getStoreClassesInScenario.find(
              storeClass => storeClass._id === args.storeClassId
            );
            return `add a new spacebreak to store class "${sc?.name || args.storeClassId}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              storeClassId: {
                type: 'string',
                description: 'The _id of the store class to add the spacebreak to.',
              },
            },
            required: ['storeClassId'],
          },
          handler: async ({ storeClassId }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (instance.proxy.isSBJobRunning)
              return '[Tool] Action failed: a spacebreak generation job is running. Ask the user to wait until it completes.';
            if (!storeClassId) return '[Tool] Action failed: storeClassId is required.';
            const storeClass = furnitureStore.getStoreClassesInScenario.find(
              sc => sc._id === storeClassId
            );
            if (!storeClass)
              return `[Tool] Action failed: store class with _id "${storeClassId}" not found.`;
            furnitureStore.addSpacebreak({ storeClassId });
            return `[Tool] Action succeeded: added a new empty spacebreak to store class "${storeClass.name}". You still need to set its name, fill override, and assign furniture before saving.`;
          },
        },
        'remove-spacebreak': {
          description:
            'Remove a spacebreak from a store class. All furniture assigned to it will be released back to available furniture. ' +
            'Cannot remove the last remaining spacebreak in a store class. ' +
            'Use _id fields from the storeClasses context. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Removing spacebreak',
          confirmationLabel: args => {
            const sc = furnitureStore.getStoreClassesInScenario.find(
              storeClass => storeClass._id === args.storeClassId
            );
            const sb = sc?.spacebreaks?.find(spacebreak => spacebreak._id === args.spacebreakId);
            return `remove spacebreak "${sb?.name || args.spacebreakId}" from store class "${sc?.name || args.storeClassId}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              storeClassId: { type: 'string', description: 'The _id of the store class.' },
              spacebreakId: { type: 'string', description: 'The _id of the spacebreak to remove.' },
            },
            required: ['storeClassId', 'spacebreakId'],
          },
          handler: async ({ storeClassId, spacebreakId }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (instance.proxy.isSBJobRunning)
              return '[Tool] Action failed: a spacebreak generation job is running. Ask the user to wait until it completes.';
            if (!storeClassId || !spacebreakId)
              return '[Tool] Action failed: storeClassId and spacebreakId are required.';
            const storeClass = furnitureStore.getStoreClassesInScenario.find(
              sc => sc._id === storeClassId
            );
            if (!storeClass)
              return `[Tool] Action failed: store class with _id "${storeClassId}" not found.`;
            if (!storeClass.spacebreaks || storeClass.spacebreaks.length <= 1)
              return `[Tool] Action failed: cannot remove the last spacebreak from store class "${storeClass.name}". At least one spacebreak is required.`;
            const spacebreak = storeClass.spacebreaks.find(sb => sb._id === spacebreakId);
            if (!spacebreak)
              return `[Tool] Action failed: spacebreak with _id "${spacebreakId}" not found in store class "${storeClass.name}".`;
            furnitureStore.removeAllFurnitureFromSpacebreak({ storeClassId, spacebreakId });
            furnitureStore.removeSpacebreakFromStoreClass({ storeClassId, spacebreakId });
            return `[Tool] Action succeeded: removed spacebreak "${spacebreak.name || spacebreakId}" from store class "${storeClass.name}". Its furniture has been released back to available furniture.`;
          },
        },
        'add-furniture-to-spacebreak': {
          description:
            'Assign a furniture archetype to a spacebreak. Use _id fields from the storeClasses context. ' +
            'The furniture must currently be in the availableFurniture list for that store class (not already assigned to any spacebreak). ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Assigning furniture',
          confirmationLabel: args => {
            const sc = furnitureStore.getStoreClassesInScenario.find(
              storeClass => storeClass._id === args.storeClassId
            );
            const sb = sc?.spacebreaks?.find(spacebreak => spacebreak._id === args.spacebreakId);
            const f = furnitureStore.getGeneratedFurnituresInScenario.find(
              furniture => furniture._id === args.furnitureId
            );
            return `assign furniture "${f?.name || args.furnitureId}" to spacebreak "${sb?.name || args.spacebreakId}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              storeClassId: { type: 'string', description: 'The _id of the store class.' },
              spacebreakId: {
                type: 'string',
                description: 'The _id of the spacebreak to assign the furniture to.',
              },
              furnitureId: {
                type: 'string',
                description: 'The _id of the furniture archetype to assign.',
              },
            },
            required: ['storeClassId', 'spacebreakId', 'furnitureId'],
          },
          handler: async ({ storeClassId, spacebreakId, furnitureId }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (instance.proxy.isSBJobRunning)
              return '[Tool] Action failed: a spacebreak generation job is running. Ask the user to wait until it completes.';
            if (!storeClassId || !spacebreakId || !furnitureId)
              return '[Tool] Action failed: storeClassId, spacebreakId, and furnitureId are all required.';
            const storeClass = furnitureStore.getStoreClassesInScenario.find(
              sc => sc._id === storeClassId
            );
            if (!storeClass)
              return `[Tool] Action failed: store class with _id "${storeClassId}" not found.`;
            const spacebreak = storeClass.spacebreaks?.find(sb => sb._id === spacebreakId);
            if (!spacebreak)
              return `[Tool] Action failed: spacebreak with _id "${spacebreakId}" not found in store class "${storeClass.name}".`;
            if (spacebreak.generatedFurnitureIds?.includes(furnitureId))
              return `[Tool] Action failed: furniture "${furnitureId}" is already assigned to spacebreak "${spacebreak.name || spacebreakId}".`;
            const alreadyInAnotherSB = storeClass.spacebreaks.some(
              sb => sb._id !== spacebreakId && sb.generatedFurnitureIds?.includes(furnitureId)
            );
            if (alreadyInAnotherSB)
              return `[Tool] Action failed: furniture "${furnitureId}" is already assigned to another spacebreak in store class "${storeClass.name}". Remove it first.`;
            const furniture = furnitureStore.getGeneratedFurnituresInScenario.find(
              f => f._id === furnitureId
            );
            if (!furniture)
              return `[Tool] Action failed: furniture archetype with _id "${furnitureId}" not found.`;
            furnitureStore.addOneFurnitureToSpacebreak({ storeClassId, spacebreakId, furnitureId });
            return `[Tool] Action succeeded: assigned furniture "${furniture.name}" to spacebreak "${spacebreak.name || spacebreakId}" in store class "${storeClass.name}".`;
          },
        },
        'remove-furniture-from-spacebreak': {
          description:
            'Remove a furniture archetype from a spacebreak. It will be released back to the available furniture list for that store class. ' +
            'Use _id fields from the storeClasses context. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Removing furniture',
          confirmationLabel: args => {
            const sc = furnitureStore.getStoreClassesInScenario.find(
              storeClass => storeClass._id === args.storeClassId
            );
            const sb = sc?.spacebreaks?.find(spacebreak => spacebreak._id === args.spacebreakId);
            const f = furnitureStore.getGeneratedFurnituresInScenario.find(
              furniture => furniture._id === args.furnitureId
            );
            return `remove furniture "${f?.name || args.furnitureId}" from spacebreak "${sb?.name || args.spacebreakId}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              storeClassId: { type: 'string', description: 'The _id of the store class.' },
              spacebreakId: { type: 'string', description: 'The _id of the spacebreak.' },
              furnitureId: {
                type: 'string',
                description: 'The _id of the furniture archetype to remove.',
              },
            },
            required: ['storeClassId', 'spacebreakId', 'furnitureId'],
          },
          handler: async ({ storeClassId, spacebreakId, furnitureId }) => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (instance.proxy.isSBJobRunning)
              return '[Tool] Action failed: a spacebreak generation job is running. Ask the user to wait until it completes.';
            if (!storeClassId || !spacebreakId || !furnitureId)
              return '[Tool] Action failed: storeClassId, spacebreakId, and furnitureId are all required.';
            const storeClass = furnitureStore.getStoreClassesInScenario.find(
              sc => sc._id === storeClassId
            );
            if (!storeClass)
              return `[Tool] Action failed: store class with _id "${storeClassId}" not found.`;
            const spacebreak = storeClass.spacebreaks?.find(sb => sb._id === spacebreakId);
            if (!spacebreak)
              return `[Tool] Action failed: spacebreak with _id "${spacebreakId}" not found in store class "${storeClass.name}".`;
            if (!spacebreak.generatedFurnitureIds?.includes(furnitureId))
              return `[Tool] Action failed: furniture "${furnitureId}" is not assigned to spacebreak "${spacebreak.name || spacebreakId}".`;
            const furniture = furnitureStore.getGeneratedFurnituresInScenario.find(
              f => f._id === furnitureId
            );
            furnitureStore.removeOneFurnitureFromSpacebreak({
              storeClassId,
              spacebreakId,
              furnitureId,
            });
            return `[Tool] Action succeeded: removed furniture "${furniture?.name || furnitureId}" from spacebreak "${spacebreak.name || spacebreakId}" in store class "${storeClass.name}". It is now in available furniture.`;
          },
        },
        'save-spacebreaks': {
          description:
            'Save all current spacebreak changes to the database. ' +
            'IMPORTANT: Do NOT save automatically after individual operations. ' +
            'Only call this when: (1) the user explicitly asks to save, (2) the user uses language like "go ahead", "apply", or "save it", ' +
            'or (3) you have completed ALL steps of an explicitly user-approved multi-step plan and saving was part of the agreed plan. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Saving spacebreaks',
          confirmationLabel: 'save all spacebreak changes',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (instance.proxy.isSaving)
              return '[Tool] Action failed: a save is already in progress. Ask the user to wait.';
            if (instance.proxy.hasDataErrors)
              return '[Tool] Action failed: the page has validation errors that must be resolved before saving. Check validationErrors and validationDetails in the current context.';
            await instance.proxy.save();
            if (instance.proxy.dependencyTreeModalOpen)
              return '[Tool] Action succeeded with required user input: the save detected that downstream work will be affected by these spacebreak changes. A confirmation modal is now open on screen listing what will be reset. The user needs to review it and either confirm the changes or cancel. Let the user know they need to action the modal before you can continue.';
            return '[Tool] Action succeeded: spacebreaks saved.';
          },
        },
        'discard-spacebreak-changes': {
          description:
            'Discard all unsaved spacebreak changes, reverting to the last saved state. ' +
            'Only call this when the user explicitly asks to discard, undo, revert, or cancel their changes. ' +
            'Do not call this proactively. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Discarding changes',
          confirmationLabel: 'discard all unsaved spacebreak changes',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (!instance.proxy.hasDataChanges)
              return '[Tool] Action failed: there are no unsaved changes to discard.';
            instance.proxy.discard();
            return '[Tool] Action succeeded: all unsaved spacebreak changes have been discarded.';
          },
        },
        'recalculate-spacebreaks': {
          description:
            'Trigger the spacebreak generation job to recalculate all spacebreaks from data. ' +
            'WARNING: This will OVERWRITE all existing spacebreaks with newly generated ones. Any manual edits (names, fill overrides, furniture assignments) will be lost. ' +
            'Only call this when the user explicitly asks to recalculate or regenerate spacebreaks. ' +
            'Do not call this proactively or as a fix for validation errors. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Starting recalculation',
          confirmationLabel:
            'recalculate all spacebreaks (this will overwrite existing spacebreaks)',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (instance.proxy.isEditingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (instance.proxy.isSBJobRunning)
              return '[Tool] Action failed: a spacebreak generation job is already running.';
            await instance.proxy.onCalculateSpacebreakClick(false);
            if (instance.proxy.dependencyTreeModalOpen)
              return '[Tool] Action succeeded with required user input: the job start detected downstream impacts. A confirmation modal is now open. The user needs to action the modal before the job will run.';
            return '[Tool] Action succeeded: spacebreak generation job started. The page will update when the job completes.';
          },
        },
      },
    });

    return {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      canEditSpacebreakCalculatorPage,
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      tkey,
      aiPanelEnabled,
      formatScenarioProgress,
    };
  },
  data() {
    return {
      selectedTab: 0,
      uiFields: ['fillSize', 'storeCount', 'mostCommonFurniture'],
      initialStoreClasses: [],
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
      calledFromOnCalculateSpacebreakClick: false,
      currentStateDiff: {},
      spacebreakConfigurations: {
        groupingThreshold: null,
        minimumStores: null,
      },
      rules: {
        groupingThreshold: [this.isPositive, this.isNumber, this.required],
        minimumStores: [this.isPositive, this.isInteger, this.required],
      },
      isSaving: false,
      spacebreakOverrideValidations: [
        s => this.required(numberUtils.formatStringToNumber(s.fillOverride)),
        s => this.isPositive(numberUtils.formatStringToNumber(s.fillOverride)),
        s => this.isNumber(numberUtils.formatStringToNumber(s.fillOverride)),
        s => this.isLessThan(numberUtils.formatStringToNumber(s.fillOverride), s.size),
        s => this.isNotEmpty(numberUtils.formatStringToNumber(s.fillOverride)),
        s => this.isGreaterThan(numberUtils.formatStringToNumber(s.fillOverride), 0),
        (s, spacebreaks) => this.isOverrideFillUnique(s, reject(spacebreaks, { _id: s._id })),
      ],
      palletsValidations: [
        s => this.required(numberUtils.formatStringToNumber(s.palletsSlots)),
        s => this.isGreaterOrEqual(numberUtils.formatStringToNumber(s.palletsSlots), 0),
        s => this.isNumber(numberUtils.formatStringToNumber(s.palletsSlots)),
        s => this.isNotEmpty(numberUtils.formatStringToNumber(s.palletsSlots)),
        (s, spacebreaks) => this.isBiggerOrEqualToThanPreviousSpaceBreak(s, spacebreaks),
      ],
    };
  },

  computed: {
    ...mapState(useFurnitureStore, [
      'getStoreClassesInScenario',
      'getGeneratedFurnituresInScenario',
      'getAllAvailableFurnitureIds',
      'hasFurnitureType',
      'isLoading',
    ]),
    ...mapState(useScenariosStore, ['jobStatuses', 'isJobRunning', 'selectedScenario']),
    ...mapState(useWorkpackagesStore, ['getUnitOfMeasure', 'selectedWorkpackage']),
    ...mapState(useContextStore, [
      'showNotImplemented',
      'getPalletsSlotsEnabled',
      'getClientConfig',
    ]),
    ...mapState(useAiStore, ['isSidePanelOpen']),

    hasDataChanges() {
      // For each initial storeclass, check if there are any data changes in spacebreaks, return early if there are
      // Data changes mean removed spacebreak, or changed spacebreak data
      return (
        this.areSpacebreaksEqualInStoreClasses({ excludeNames: false, excludePallets: false }) ||
        !isEmpty(this.currentStateDiff)
      );
    },

    hasDataErrors() {
      return (
        !isEmpty(this.getAllAvailableFurnitureIds) ||
        !this.allSpacebreakNamesUnique ||
        !this.allSpacebreakOverrideAreValid ||
        !this.allPalletsAreValid ||
        !this.allSpacebreaksAreFilled ||
        (this.showSpacebreakConfigurations &&
          some(this.rules, (rules, key) =>
            some(rules, r => r(this.spacebreakConfigurations[key]) !== true)
          ))
      );
    },

    isSBJobRunning() {
      return this.isJobRunning('spacebreakGenerator');
    },

    allSpacebreakNamesUnique() {
      return this.getStoreClassesInScenario.every(
        sc => uniqBy(sc.spacebreaks, 'name').length === sc.spacebreaks.length
      );
    },

    hasSpacebreaks() {
      return (
        !this.isSBJobRunning &&
        !isEmpty(this.getStoreClassesInScenario) &&
        every(this.getStoreClassesInScenario, storeClass => !isEmpty(storeClass.spacebreaks))
      );
    },

    allSpacebreakOverrideAreValid() {
      return this.getStoreClassesInScenario.every(({ spacebreaks }) =>
        spacebreaks.every(s =>
          this.spacebreakOverrideValidations.every(f => f(s, spacebreaks) === true)
        )
      );
    },

    storeClassesWithInvalidFillOverrideMap() {
      return this.getStoreClassesInScenario.reduce((acc, sc) => {
        const invalidSBs = sc.spacebreaks.filter(
          s =>
            // Checking it as true value since some of the validation functions will return true if it's valid
            // and a error translation string message if not
            !this.spacebreakOverrideValidations.every(f => f(s, sc.spacebreaks) === true)
        );
        if (!isEmpty(invalidSBs)) {
          acc[sc._id] = invalidSBs.map(s => s._id);
        }
        return acc;
      }, {});
    },

    storeClassesWithInvalidPalletsMap() {
      if (!this.getPalletsSlotsEnabled) {
        return {};
      }

      return this.getStoreClassesInScenario.reduce((acc, sc) => {
        const invalidSBs = sc.spacebreaks.filter(
          s =>
            // Checking it as true value since some of the validation functions will return true if it's valid
            // and a error translation string message if not
            !this.palletsValidations.every(f => f(s, sc.spacebreaks) === true)
        );
        if (!isEmpty(invalidSBs)) {
          acc[sc._id] = invalidSBs.map(s => s._id);
        }
        return acc;
      }, {});
    },

    storeClassesWithInvalidSpacebreaksMap() {
      const fillOverrideInvalidMap = this.storeClassesWithInvalidFillOverrideMap;
      const palletsInvalidMap = this.storeClassesWithInvalidPalletsMap;
      const fillOverrideIvalidKeys = keys(fillOverrideInvalidMap);

      return fillOverrideIvalidKeys.reduce((acc, key) => {
        acc[key] = union(fillOverrideInvalidMap[key], palletsInvalidMap[key] || []);
        return acc;
      }, {});
    },

    /*
    Pallets should be:
    - positive numbers
    - required
    - not empty
    - all bigger than the previous spacebreak's pallets
     */
    allPalletsAreValid() {
      if (this.getPalletsSlotsEnabled) {
        return this.getStoreClassesInScenario.every(({ spacebreaks }) =>
          spacebreaks.every(s => {
            return this.palletsValidations.every(f => f(s, spacebreaks) === true);
          })
        );
      }
      return true;
    },

    allSpacebreaksAreFilled() {
      return this.getStoreClassesInScenario.every(({ spacebreaks }) => {
        return spacebreaks.every(s => {
          return s.generatedFurnitureIds.length > 0 && !isEmpty(s.name);
        });
      });
    },

    isEditingDisabled() {
      return (
        !this.canEditSpacebreakCalculatorPage ||
        !get(this.selectedScenario, 'status.space.canBeEdited', true)
      );
    },

    showSpacebreakConfigurations() {
      // Spacebreak configuration inputs should only be visible if the feature flag is enabled
      // AND there are no manually added furnitures
      return (
        get(this.getClientConfig, 'features.spacebreakConfigurationsEnabled', false) &&
        !this.hasFurnitureType(furnitureType.manual)
      );
    },

    // AI Panel computed properties
    workpackageScopeUnit() {
      return this.getUnitOfMeasure(this.selectedWorkpackage);
    },

    totalSpacebreaks() {
      return this.getStoreClassesInScenario.reduce(
        (total, sc) => total + (sc.spacebreaks?.length || 0),
        0
      );
    },

    totalStoresAcrossAllSpacebreaks() {
      return this.getStoreClassesInScenario.reduce((total, sc) => {
        const storeCount = sc.spacebreaks?.reduce((sum, sb) => sum + (sb.storeCount || 0), 0) || 0;
        return total + storeCount;
      }, 0);
    },
  },

  watch: {},

  created() {
    this.initialStoreClasses = cloneDeep(this.getStoreClassesInScenario);
    if (this.showSpacebreakConfigurations) this.initialiseSpacebreakConfigurations();

    // Initialize AI Panel (outer shell only; inner context is built lazily at send-time)
    const aiStore = useAiStore();
    aiStore.enableSidePanel({
      section: 'spacebreak-calculator',
      contextDisplay: [{ label: 'Spacebreak Overview', value: '' }],
    });
    aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
    // Safe to set immediately: ScenarioWorkflow + ScenarioSpace v-if gates ensure
    // selectedScenario and furniture store data are both populated before this page mounts.
    aiStore.setContextReady(true);
  },

  beforeUnmount() {
    // (no AI panel teardown needed — route watcher in AiSidePanel handles context reset)
  },

  methods: {
    ...mapActions(useFurnitureStore, [
      'saveScenarioFurniture',
      'fetchScenarioFurniture',
      'startSpacebreaksJob',
    ]),
    ...mapActions(useScenariosStore, ['updateScenario', 'refreshScenario']),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),

    buildInnerContext() {
      const unassignedFurnitureCount = size(this.getAllAvailableFurnitureIds);
      const generatedFurnitureById = keyBy(this.getGeneratedFurnituresInScenario, '_id');

      const storeClasses = this.getStoreClassesInScenario.map(sc => {
        const furnitureInSpacebreaks = new Set(flatMap(sc.spacebreaks, 'generatedFurnitureIds'));
        const availableFurniture = (sc.generatedFurnitureIds || [])
          .filter(id => !furnitureInSpacebreaks.has(id))
          .map(id => ({
            _id: id,
            name: get(generatedFurnitureById, [id, 'name'], id),
            size: get(generatedFurnitureById, [id, 'size'], null),
          }));
        return {
          _id: sc._id,
          name: sc.name,
          spacebreakCount: sc.spacebreaks?.length || 0,
          availableFurniture,
          spacebreaks:
            sc.spacebreaks?.map(sb => ({
              _id: sb._id,
              name: sb.name || 'Unnamed',
              fillOverride: sb.fillOverride || null,
              storeCount: sb.storeKeys?.length || 0,
              assignedFurniture: (sb.generatedFurnitureIds || []).map(id => ({
                _id: id,
                name: get(generatedFurnitureById, [id, 'name'], id),
                size: get(generatedFurnitureById, [id, 'size'], null),
              })),
            })) || [],
        };
      });

      const validationErrors = [];
      if (!this.allSpacebreakNamesUnique)
        validationErrors.push('Duplicate spacebreak names detected');
      if (!this.allSpacebreakOverrideAreValid)
        validationErrors.push('Invalid fill override values detected');
      if (!this.allPalletsAreValid && this.getPalletsSlotsEnabled)
        validationErrors.push('Invalid pallets/slots values detected');
      if (!this.allSpacebreaksAreFilled)
        validationErrors.push('Empty spacebreaks detected (missing furniture or name)');
      if (unassignedFurnitureCount > 0)
        validationErrors.push(
          `${unassignedFurnitureCount} furniture archetypes not assigned to any spacebreak`
        );
      if (
        this.showSpacebreakConfigurations &&
        some(this.rules, (rules, key) =>
          some(rules, r => r(this.spacebreakConfigurations[key]) !== true)
        )
      ) {
        validationErrors.push('Invalid configuration parameters');
      }

      // Pre-compute specific offenders so the AI can name them directly
      const validationDetails = [];
      this.getStoreClassesInScenario.forEach(sc => {
        // Duplicate fill override values
        const fillGroups = {};
        sc.spacebreaks.forEach(sb => {
          if (sb.fillOverride != null) {
            if (!fillGroups[sb.fillOverride]) fillGroups[sb.fillOverride] = [];
            fillGroups[sb.fillOverride].push(sb.name || 'Unnamed');
          }
        });
        Object.entries(fillGroups)
          .filter(([, names]) => names.length > 1)
          .forEach(([fill, names]) => {
            validationDetails.push(
              `Duplicate fill override ${fill} in store class "${sc.name}": ${names.join(', ')}`
            );
          });

        // Missing or zero fill overrides
        sc.spacebreaks
          .filter(sb => !sb.fillOverride)
          .forEach(sb => {
            validationDetails.push(
              `Missing or zero fill override in store class "${sc.name}": "${sb.name || 'Unnamed'}"`
            );
          });

        // Duplicate spacebreak names
        const nameGroups = {};
        sc.spacebreaks.forEach(sb => {
          const n = sb.name || 'Unnamed';
          nameGroups[n] = (nameGroups[n] || 0) + 1;
        });
        Object.entries(nameGroups)
          .filter(([, count]) => count > 1)
          .forEach(([name]) => {
            validationDetails.push(
              `Duplicate spacebreak name "${name}" in store class "${sc.name}"`
            );
          });
      });

      return {
        scenarioName: this.selectedScenario?.name || 'Unknown',
        workpackageScope: this.workpackageScopeUnit,
        scenarioProgress: this.formatScenarioProgress,
        totalSpacebreaks: this.totalSpacebreaks,
        storeClasses,
        unassignedFurnitureCount,
        hasUnsavedChanges: this.hasDataChanges,
        jobRunning: this.isSBJobRunning
          ? { progress: get(this.jobStatuses, 'spacebreakGenerator.progress', 0) }
          : null,
        configuration: this.showSpacebreakConfigurations
          ? {
              groupingThreshold: this.spacebreakConfigurations.groupingThreshold || null,
              minimumStores: this.spacebreakConfigurations.minimumStores || null,
            }
          : null,
        validationErrors,
        validationDetails: validationDetails.length > 0 ? validationDetails : null,
        canSave: validationErrors.length === 0,
      };
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    async commitHandler() {
      if (this.calledFromOnCalculateSpacebreakClick) await this.onCalculateSpacebreakClick(true);
      else this.save(true);
    },

    sanitiseStoreClasses(storeClasses, orderSpacebreaks = false) {
      // remove unwanted keys needed for the UI
      const unitOfMeasure = this.getUnitOfMeasure(this.selectedWorkpackage);

      return map(storeClasses, sc => {
        const spacebreaks = map(sc.spacebreaks, sb => {
          return {
            ...omit(sb, this.uiFields),
            shortName: `${round(sb.fillOverride)}${unitOfMeasure}`,
            // nedd to remove the temporary _ids as the server will create Mongo ObjectIds
            _id: startsWith(sb._id, newlyAddedSpacebreakIdPrefix) ? null : sb._id,
          };
        });

        sc.spacebreaks = orderSpacebreaks
          ? orderBy(spacebreaks, ['fillOverride'], ['asc'])
          : spacebreaks;
        return sc;
      });
    },

    initialiseSpacebreakConfigurations() {
      const defaultGroupingThreshold = get(this.getClientConfig, 'spacebreaks.groupingThreshold');
      const defaultMinStores = get(this.getClientConfig, 'spacebreaks.minimumStores');

      // Use values from the client config to set the default values for the spacebreak configurations
      this.spacebreakConfigurations.groupingThreshold = get(
        this.selectedScenario,
        'spacebreakSettings.groupingThreshold',
        defaultGroupingThreshold
      );
      this.spacebreakConfigurations.minimumStores = get(
        this.selectedScenario,
        'spacebreakSettings.minimumStores',
        defaultMinStores
      );

      // Backup configuration data
      this.$options.savedState = cloneDeep(this.spacebreakConfigurations);
    },

    discard() {
      this.fetchScenarioFurniture();
      this.reset();
    },

    reset() {
      this.initialStoreClasses = cloneDeep(this.getStoreClassesInScenario);
      this.currentStateDiff = {};

      // Reset configurations
      if (this.showSpacebreakConfigurations) this.initialiseSpacebreakConfigurations();
    },

    async updateSpacebreakConfigurations() {
      const updates = {
        id: this.selectedScenario._id,
        'spacebreakSettings.groupingThreshold': this.spacebreakConfigurations.groupingThreshold,
        'spacebreakSettings.minimumStores': this.spacebreakConfigurations.minimumStores,
      };

      this.updateScenario({ scenario: updates });
    },

    async save(commit = false) {
      this.isSaving = true;
      const storeClasses = map(this.getStoreClassesInScenario, sc => {
        sc.spacebreaks = orderBy(sc.spacebreaks, ['fillSuggested'], ['asc']);
        return sc;
      });

      // Spacebreaks need to be ordered globally. These are used in store executions.
      function addSequentialSpacebreakKeys(allStoreClasses) {
        const spacebreaks = flatMap(allStoreClasses, 'spacebreaks');
        const sortedSpacebreaks = sortBy(spacebreaks, 'fillOverride');

        each(sortedSpacebreaks, (sb, ix) => {
          sb.sequentialKey = `SB${padStart(ix + 1, 2, 0)}`;
        });
      }

      addSequentialSpacebreakKeys(storeClasses);

      const sanitisedArray = this.sanitiseStoreClasses(storeClasses, true);

      // Trigger dependency tree only in case spacebreaks data was changed
      if (this.areSpacebreaksEqualInStoreClasses({ excludeNames: true, excludePallets: true })) {
        // Perform dependency tree check
        const results = await this.triggerDependencyTree({
          params: { change: 'spacebreaksModified', updates: {}, commit },
        });

        if (results.needsFeedback) {
          this.dependencyTreeFeedback = results.output;
          this.dependencyTreeModalOpen = true;
          this.isSaving = false;
          return;
        }
      }

      const spacebreakPromises = [
        await this.saveScenarioFurniture({ storeClasses: sanitisedArray }),
      ];
      if (this.showSpacebreakConfigurations) {
        spacebreakPromises.push(await this.updateSpacebreakConfigurations());
      }
      await Promise.all(spacebreakPromises);
      await this.refreshScenario();
      this.reset();
      this.isSaving = false;
    },

    async onCalculateSpacebreakClick(commit = false) {
      this.calledFromOnCalculateSpacebreakClick = true; // for dependency tree modal
      // Perform dependency tree check
      const results = await this.triggerDependencyTree({
        params: { change: 'spacebreaksModified', updates: {}, commit },
      });

      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return;
      }
      // Make sure that the current spacebreak configurations are saved before the job is triggered
      if (this.showSpacebreakConfigurations) await this.updateSpacebreakConfigurations();
      // Clear currentStateDiff after saving configurations to ensure buttons have the correct disabled state
      this.currentStateDiff = {};
      await this.startSpacebreaksJob();
    },

    isOverrideFillUnique(spacebreak, otherSpacebreaks) {
      const spacebreaksWithSameSize = filter(otherSpacebreaks, {
        fillOverride: spacebreak.fillOverride,
      });

      return isEmpty(spacebreaksWithSameSize);
    },

    isBiggerOrEqualToThanPreviousSpaceBreak(currentSpacebreak, allSpacebreaks) {
      const currentSpacebreakIndex = findIndex(allSpacebreaks, { _id: currentSpacebreak._id });
      const palletsToCompare = get(allSpacebreaks[currentSpacebreakIndex - 1], 'palletsSlots', 0);

      const sanitisedCurrentPalletValue = numberUtils.formatStringToNumber(
        currentSpacebreak.palletsSlots
      );
      const sanitisedPreviousPalletValue = numberUtils.formatStringToNumber(palletsToCompare);

      return (
        sanitisedCurrentPalletValue >= 0 &&
        sanitisedCurrentPalletValue >= sanitisedPreviousPalletValue
      );
    },

    areSpacebreaksEqualInStoreClasses({ excludeNames = false, excludePallets = false } = {}) {
      const sanitisedInitialSC = this.sanitiseStoreClasses(cloneDeep(this.initialStoreClasses));
      const sanitisedCurrentSC = this.sanitiseStoreClasses(
        cloneDeep(this.getStoreClassesInScenario)
      );

      const initialSpacebreaks = sanitisedInitialSC.map(sc => get(sc, 'spacebreaks', []).length);
      const modifiedSpacebreaks = sanitisedCurrentSC.map(sc => get(sc, 'spacebreaks', []).length);
      const hasNewSpacebreaks =
        difference(initialSpacebreaks, modifiedSpacebreaks).length > 0 ||
        difference(modifiedSpacebreaks, initialSpacebreaks).length > 0;

      if (hasNewSpacebreaks) return true;
      // For each initial storeclass, check if there are any changes in spacebreaks, return early if there are
      return some(sanitisedInitialSC, initialSC => {
        const currentSC = find(sanitisedCurrentSC, { _id: initialSC._id });
        if (!currentSC) return false;

        return some(initialSC.spacebreaks, initialSB => {
          const currentSB = find(currentSC.spacebreaks, { _id: initialSB._id });

          if (!currentSB) return true; // return true if spacebreak was removed

          // First we check that spacebreaks are the same excluding generatedFurnitureIds
          // We also exclude names in case we check for dependency tree
          const fieldsToExclude = ['generatedFurnitureIds', 'storeKeys'];
          if (excludeNames) fieldsToExclude.push('name');
          if (excludePallets && this.getPalletsSlotsEnabled) fieldsToExclude.push('palletsSlots');

          const isPureEqual = isEqual(
            omit(initialSB, fieldsToExclude),
            omit(currentSB, fieldsToExclude)
          );

          const initialGfIds = initialSB.generatedFurnitureIds;
          const currentGfIds = currentSB.generatedFurnitureIds;

          // Then we check that all current ids are also in initial and that sizes are the same
          // This is because after drag and drop furniture id goes to the end of the list, so we can't compare using _.isEqual
          // We don't check store keys here because they depend on furniture ids
          const areGfIdsTheSame =
            isEqual(size(initialGfIds), size(currentGfIds)) &&
            every(currentGfIds, currentId => initialGfIds.includes(currentId));

          return !isPureEqual || !areGfIdsTheSame;
        });
      });
    },

    updateDiff(field, currentValue) {
      const originalValue = get(this.$options.savedState, field);
      const existingValue = get(this.currentStateDiff, field);
      const valueHasChanged = currentValue !== originalValue;

      if (valueHasChanged) {
        this.currentStateDiff[field] = currentValue;
      } else if (!isUndefined(existingValue)) {
        // Remove the field from the object if it's back to its old value.
        this.$delete(this.currentStateDiff, field);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

$tab-height: 26px;
$tab-width: 200px;

.calculate-btn-container {
  padding-left: 14px;
  padding-right: 15px;
  border-right: 1px solid $assortment-horizontal-border-colour;
}

.page-actions-container {
  border-top: 1px solid $assortment-panel-border-divider-colour;
}

.main-panel {
  height: 500px;
  overflow: hidden;

  .v-window {
    overflow-y: auto;

    :deep(.v-window__container) {
      height: 100%;
    }
  }
}

.store-class-tabs {
  display: flex;
  flex-direction: column;

  .store-class-tab {
    padding: 0 10px;
    justify-content: inherit;
    text-align: inherit;
    text-transform: none;
    box-sizing: border-box;
    height: $tab-height;
    width: $tab-width;
    background: $assortment-unselected-tab-colour;
    border: 0.3px solid $assortment-border-colour;
    border-left: 1px solid $assortment-tile-border-colour;
    border-bottom: 1px solid $assortment-tile-border-colour;
    color: $assortment-inactive-tab-text-colour !important; // non-selected tab should be slightly faded
    font-size: 13px;
    font-weight: 900;

    &:not(:first-child) {
      margin-left: 4px;
    }

    &:first-child {
      border-left: 0px;
    }
    &.v-tab--selected {
      background: $assortment-selected-tab-colour;
      border-bottom: 0px;
      color: $assortment-text-colour !important;
    }
  }

  // Specific styling for v-slide-group content to render a border below the tabs
  :deep() {
    .v-slide-group__content {
      height: $tab-height; // same as tab height
      border-bottom: 1px solid $assortment-tile-border-colour;
    }

    // Tabs text should be aligned to the left
    .v-btn__content {
      justify-content: start;
    }
  }
}

.discard-btn {
  margin-left: 15px;
}

.empty-switching {
  background-color: $assortment-background;
  min-height: 500px;
}

.empty-switching-text {
  font-size: 1.4rem;
  color: $assortment-text-colour;
}

.spacebreak-configurations {
  flex: 0 0 auto;
  width: auto;

  &__input {
    width: 80px;
  }

  &__label {
    display: inline-block;
    font-weight: bold;
  }

  &__message {
    font-size: 1.2rem;
  }
}

.spacebreak-actions {
  flex: 1 1 auto;
  width: auto;
}

:deep() {
  .error-triangle {
    font-size: 18px;
  }
}
</style>
